/**
 * Protocol Change Notice (Treatment Plan spec v1.4, S-26).
 *
 * When the MCOS Protocol Library changes in a way that matters, or the
 * formulary blocks an item on an active plan, the prescriber sees what changed,
 * why, and which of their patients are on it. Each patient takes one answer:
 * "Reviewed, no change" or "Revise plan". The answer is logged (PL-004).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from './ui';

interface Notice {
  id: string;
  title: string;
  summary: string;
  sentAt: string;
  patients: Array<{ planId: string; patientId: string; patientName: string; item: string; why: string; action: 'no_change' | 'revise' | null }>;
}

export function ProtocolNotices({ patientId, onRevise }: { patientId?: string; onRevise?: (patientId: string) => void }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const key = ['protocol-notices', patientId ?? 'all'];
  const notices = useQuery({
    queryKey: key,
    // Prescribers only: front desk staff cannot act on a notice.
    enabled: can('ADMIN', 'CLINICIAN', 'MEDICAL_DIRECTOR'),
    queryFn: () => api.get<{ notices: Notice[] }>(`/signoff/notices${patientId ? `?patientId=${encodeURIComponent(patientId)}` : ''}`),
  });
  const answer = useMutation({
    mutationFn: (input: { noticeId: string; planId: string; patientId: string; action: 'no_change' | 'revise' }) =>
      api.post(`/signoff/notices/${input.noticeId}/acknowledge`, { planId: input.planId, action: input.action }),
    onSuccess: (_result, input) => {
      void qc.invalidateQueries({ queryKey: ['protocol-notices'] });
      if (input.action === 'revise') onRevise?.(input.patientId);
    },
  });

  const open = (notices.data?.notices ?? []).filter((n) => n.patients.some((p) => !p.action));
  if (open.length === 0) return null;
  return (
    <div className="mb-2.5 space-y-2" data-testid="protocol-notices">
      {open.map((notice) => (
        <section key={notice.id} className="rounded-[10px] border border-[#e3d3a8] bg-[#fffaf0] px-4 py-3" role="status">
          <p className="text-[14px] font-semibold text-navy">{notice.title}</p>
          {notice.summary && <p className="mt-0.5 text-[13px] text-[#4d6072]">{notice.summary}</p>}
          <ul className="mt-2 space-y-2">
            {notice.patients.filter((p) => !p.action).map((p) => (
              <li key={p.planId} className="flex flex-wrap items-center justify-between gap-2 text-[13px]">
                <span className="text-navy">
                  {p.patientName}, {p.item}: <span className="text-[#4d6072]">{p.why}</span>
                </span>
                <span className="flex gap-2">
                  <Button size="sm" variant="secondary" loading={answer.isPending} onClick={() => answer.mutate({ noticeId: notice.id, planId: p.planId, patientId: p.patientId, action: 'no_change' })}>
                    Reviewed, no change
                  </Button>
                  <Button size="sm" loading={answer.isPending} onClick={() => answer.mutate({ noticeId: notice.id, planId: p.planId, patientId: p.patientId, action: 'revise' })}>
                    Revise plan
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
      {answer.error && <p className="text-sm text-red-600" role="alert">{answer.error instanceof Error ? answer.error.message : 'Could not save'}</p>}
    </div>
  );
}
